import { useMemo, useState, type DragEvent, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Plus, Share2, AlertTriangle, ExternalLink, Pencil, Trash2, LayoutGrid, List, Play,
  Image as ImageIcon, Send, Clock, Folder, FolderPlus, FolderOpen, ChevronRight, ChevronDown,
  Search, Check,
} from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardBody } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Select } from '@/components/ui/Select';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/useToast';
import { SocialPostThumb } from '@/pages/marketing/SocialPostThumb';
import { SocialMediaPreviewModal, previewItemsFromZernio, type PreviewMediaItem } from '@/pages/marketing/SocialMediaPreview';
import { ZernioPostComposer } from '@/pages/marketing/ZernioPostComposer';
import {
  getZernioCredentials,
  listZernioPosts,
  platformLabel,
  canEditPost,
  canDeletePost,
  deleteZernioPost,
  type PostStatus,
  type ZernioPost,
} from '@/services/zernio.service';
import {
  getSocialPostDrafts,
  deleteSocialPostDraft,
  getMyPostFolders,
  createMyPostFolder,
  renameMyPostFolder,
  deleteMyPostFolder,
  placeMyPosts,
  type MyPostFolder,
  type MyPostItemType,
  type SocialPostDraft,
} from '@/services/marketing.service';

/**
 * My Posts: posts published or scheduled through Zernio, plus the ones saved
 * for later in Prospera, organised in folders.
 *
 * ── Folders ──
 * Drive-style, like Social Post Templates: the page opens on folder tiles and
 * clicking one opens it. These are My Posts' OWN folders, one private set per
 * account, not the template library's. General always exists and holds any post
 * not filed elsewhere. The open folder is in the URL (?postFolder=id — not
 * `folder`, which the Templates tab uses), so Back returns to the tiles.
 * Deleting a folder moves its posts to General; nothing is cancelled in Zernio.
 *
 * SAVED FOR LATER comes first in a folder, marked "To be scheduled later".
 * Those are not in Zernio at all — Zernio keeps no unscheduled post — so they
 * come from our own table. Opening one reopens the composer exactly as it was
 * left; publishing or scheduling it removes the saved copy.
 *
 * Zernio returns the latest POST_LIMIT posts, so folder counts cover those.
 */

/** How many recent Zernio posts are loaded (the API allows up to 200). */
const POST_LIMIT = 100;

/** A status filter: Zernio's own, plus our "To be scheduled later". */
type StatusFilter = PostStatus | 'later';

const STATUS_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'later', label: 'To be scheduled later' },
  { value: 'published', label: 'Published' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'draft', label: 'Draft' },
  { value: 'failed', label: 'Failed' },
];

type ViewMode = 'grid' | 'list';

/** One thing My Posts lists: a saved-for-later post or a Zernio post. */
type Item =
  | { kind: 'draft'; key: string; draft: SocialPostDraft }
  | { kind: 'zernio'; key: string; post: ZernioPost };

const DRAG_TYPE = 'application/x-my-post-items';

/** General is folder 0 — always present, never renamed or deleted. */
const GENERAL: MyPostFolder = { id: 0, name: 'General' };

function statusVariant(status: string) {
  if (status === 'published') return 'success' as const;
  if (status === 'failed') return 'error' as const;
  if (status === 'scheduled' || status === 'queued' || status === 'pending') return 'warning' as const;
  return 'neutral' as const;
}

function itemRef(item: Item): { type: MyPostItemType; key: string } {
  return { type: item.kind === 'draft' ? 'draft' : 'zernio', key: item.key };
}

function apiMessage(e: unknown, fallback: string): string {
  return (e as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message ?? fallback;
}

interface PostsProps {
  embedded?: boolean;
}

export default function SocialPostsPage({ embedded }: PostsProps = {}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [composerOpen, setComposerOpen] = useState(false);
  const [editingPost, setEditingPost] = useState<ZernioPost | null>(null);
  const [openDraft, setOpenDraft] = useState<SocialPostDraft | null>(null);
  /** The post whose media is being played back for review. */
  const [preview, setPreview] = useState<{ title?: string; caption: string; items: PreviewMediaItem[] } | null>(null);
  const [view, setView] = useState<ViewMode>('grid');
  const [status, setStatus] = useState<StatusFilter>('all');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [moveOpen, setMoveOpen] = useState(false);
  const [dragKeys, setDragKeys] = useState<string[] | null>(null);
  const [dropTargetId, setDropTargetId] = useState<number | null>(null);
  const [nameDialog, setNameDialog] = useState<{ folder: MyPostFolder | null } | null>(null);
  const [deletingFolder, setDeletingFolder] = useState<MyPostFolder | null>(null);

  // ── Data ─────────────────────────────────────────────────────────────────
  const credentials = useQuery({
    queryKey: ['admin', 'zernio', 'credentials'],
    queryFn: getZernioCredentials,
  });

  const configured = credentials.data?.configured ?? false;
  const isVerified = credentials.data?.isVerified ?? false;
  const canPublish = configured && isVerified;

  const posts = useQuery({
    queryKey: ['zernio', 'posts', status],
    queryFn: () => listZernioPosts(POST_LIMIT, status as PostStatus),
    // "To be scheduled later" is ours alone; Zernio has nothing to add to it.
    enabled: configured && status !== 'later',
  });

  const drafts = useQuery({
    queryKey: ['marketing', 'social-post-drafts'],
    queryFn: getSocialPostDrafts,
    enabled: configured,
  });

  const folderState = useQuery({
    queryKey: ['marketing', 'my-post-folders'],
    queryFn: getMyPostFolders,
    enabled: configured,
  });

  const folders = useMemo(() => [GENERAL, ...(folderState.data?.folders ?? [])], [folderState.data]);

  const placement = useMemo(() => {
    const map = new Map<string, number>();
    for (const p of folderState.data?.placements ?? []) map.set(`${p.type}:${p.key}`, p.folderId);
    return map;
  }, [folderState.data]);

  const folderOf = (item: Item): number => {
    const ref = itemRef(item);
    const id = placement.get(`${ref.type}:${ref.key}`) ?? 0;
    // A placement pointing at a deleted folder falls back to General.
    return folders.some((f) => f.id === id) ? id : 0;
  };

  const showDrafts = status === 'all' || status === 'later';
  const items: Item[] = useMemo(() => [
    ...(showDrafts ? drafts.data ?? [] : []).map((d) => ({ kind: 'draft' as const, key: String(d.id), draft: d })),
    ...(status === 'later' ? [] : posts.data?.posts ?? []).map((p) => ({ kind: 'zernio' as const, key: p.postId, post: p })),
  ], [showDrafts, status, drafts.data, posts.data]);

  const q = search.trim().toLowerCase();
  const matches = (item: Item) =>
    !q || (item.kind === 'draft' ? item.draft.caption : `${item.post.title ?? ''} ${item.post.content ?? ''}`)
      .toLowerCase().includes(q);

  const requestedFolder = searchParams.get('postFolder');
  const openFolder = requestedFolder === null
    ? null
    : folders.find((f) => f.id === Number(requestedFolder)) ?? null;

  const inFolder = (folderId: number) => items.filter((i) => folderOf(i) === folderId);
  const tiles = folders
    .map((f) => {
      const all = inFolder(f.id);
      return { folder: f, count: all.length, matches: all.filter(matches).length };
    })
    // General always shows; with a search or status filter, other folders only
    // when something in them fits.
    .filter((t) => t.folder.id === 0 || (q ? t.matches > 0 : status === 'all' || t.count > 0));
  const folderItems = openFolder ? inFolder(openFolder.id).filter(matches) : [];

  const loading = folderState.isLoading || (showDrafts && drafts.isLoading) || (status !== 'later' && posts.isLoading);

  // ── Navigation ───────────────────────────────────────────────────────────
  const clearSelection = () => { setSelected(new Set()); setMoveOpen(false); };

  const goToFolder = (id: number | null) => {
    const next = new URLSearchParams(searchParams);
    if (id === null) next.delete('postFolder');
    else next.set('postFolder', String(id));
    // A push, not a replace, so Back returns to the tiles.
    setSearchParams(next);
    clearSelection();
    setSearch('');
  };

  // ── Mutations ────────────────────────────────────────────────────────────
  const invalidateFolders = () => qc.invalidateQueries({ queryKey: ['marketing', 'my-post-folders'] });

  const cancelMut = useMutation({
    mutationFn: (postId: string) => deleteZernioPost(postId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['zernio', 'posts'] });
      toast.success('Scheduled post cancelled');
    },
    onError: (e: unknown) => {
      // A 409 carries a plain sentence when Zernio has already moved the post on.
      toast.error(apiMessage(e, 'Could not cancel that post.'));
      qc.invalidateQueries({ queryKey: ['zernio', 'posts'] });
    },
  });

  const deleteDraftMut = useMutation({
    mutationFn: (id: number) => deleteSocialPostDraft(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['marketing', 'social-post-drafts'] });
      invalidateFolders();
      toast.success('Saved post deleted');
    },
    onError: () => toast.error('Could not delete that saved post.'),
  });

  const moveMut = useMutation({
    mutationFn: ({ keys, target }: { keys: string[]; target: MyPostFolder }) =>
      placeMyPosts(items.filter((i) => keys.includes(`${i.kind}:${i.key}`)).map(itemRef), target.id),
    onSuccess: ({ placed }, { target }) => {
      invalidateFolders();
      clearSelection();
      toast.success(`${placed} post${placed === 1 ? '' : 's'} moved to ${target.name}`);
    },
    onError: (e) => toast.error(apiMessage(e, 'Move failed')),
  });

  const deleteFolderMut = useMutation({
    mutationFn: (f: MyPostFolder) => deleteMyPostFolder(f.id),
    onSuccess: ({ movedToGeneral }, f) => {
      invalidateFolders();
      if (openFolder?.id === f.id) goToFolder(null);
      toast.success(
        `Folder “${f.name}” deleted`,
        movedToGeneral > 0 ? `${movedToGeneral} post${movedToGeneral === 1 ? '' : 's'} moved to General.` : undefined,
      );
    },
    onError: (e) => toast.error(apiMessage(e, 'Could not delete the folder')),
  });

  // ── Drag and drop ────────────────────────────────────────────────────────
  const selKey = (i: Item) => `${i.kind}:${i.key}`;

  const startDrag = (e: DragEvent, item: Item) => {
    const keys = selected.has(selKey(item)) ? Array.from(selected) : [selKey(item)];
    e.dataTransfer.setData(DRAG_TYPE, JSON.stringify(keys));
    e.dataTransfer.effectAllowed = 'move';
    setDragKeys(keys);
  };
  const endDrag = () => { setDragKeys(null); setDropTargetId(null); };

  const dropProps = (target: MyPostFolder) => ({
    onDragOver: (e: DragEvent) => {
      if (!e.dataTransfer.types.includes(DRAG_TYPE)) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      setDropTargetId(target.id);
    },
    onDragLeave: () => setDropTargetId((id) => (id === target.id ? null : id)),
    onDrop: (e: DragEvent) => {
      e.preventDefault();
      const raw = e.dataTransfer.getData(DRAG_TYPE);
      endDrag();
      if (!raw) return;
      // Dropping onto the folder a post is already in does nothing.
      const keys = (JSON.parse(raw) as string[]).filter((k) => {
        const item = items.find((i) => selKey(i) === k);
        return item && folderOf(item) !== target.id;
      });
      if (keys.length > 0) moveMut.mutate({ keys, target });
    },
  });

  const toggleSelect = (item: Item) => setSelected((prev) => {
    const next = new Set(prev);
    const k = selKey(item);
    if (next.has(k)) next.delete(k); else next.add(k);
    return next;
  });

  const otherFolders = folders.filter((f) => f.id !== openFolder?.id);
  const deletingCount = deletingFolder ? inFolder(deletingFolder.id).length : 0;

  // ── Render ───────────────────────────────────────────────────────────────
  const newPostButton = (
    <Button onClick={() => setComposerOpen(true)} disabled={!canPublish}>
      <Plus className="h-4 w-4 mr-1" /> New Post
    </Button>
  );

  const viewToggle = (
    <div className="flex items-center rounded-md border border-slate-200 overflow-hidden" role="group" aria-label="View">
      {([['grid', LayoutGrid, 'Grid view'], ['list', List, 'List view']] as const).map(([mode, Icon, label]) => (
        <button
          key={mode}
          type="button"
          title={label}
          aria-pressed={view === mode}
          onClick={() => setView(mode)}
          className={cn(
            'px-2.5 py-2 transition-colors',
            view === mode ? 'bg-slate-100 text-slate-900' : 'bg-white text-slate-500 hover:bg-slate-50',
          )}
        >
          <Icon className="h-4 w-4" />
        </button>
      ))}
    </div>
  );

  const confirmCancel = (post: ZernioPost) => {
    // Cannot be undone: Zernio deletes the post rather than marking it cancelled.
    if (!window.confirm(
      'Cancel this scheduled post? It will be deleted in Zernio and will not be published. This cannot be undone.',
    )) return;
    cancelMut.mutate(post.postId);
  };

  const renderItem = (item: Item) => {
    const thumbClass = view === 'grid' ? 'aspect-square w-full' : 'h-16 w-16 shrink-0 rounded-md';
    const common = {
      view,
      selected: selected.has(selKey(item)),
      dragging: dragKeys?.includes(selKey(item)) ?? false,
      onToggleSelect: () => toggleSelect(item),
      dragProps: { draggable: true, onDragStart: (e: DragEvent) => startDrag(e, item), onDragEnd: endDrag },
    };
    // The thumbnail opens a playable preview of every item. A button inside the
    // draggable card: a click previews, a drag still moves the post.
    const previewable = (node: ReactNode, open: () => void) => (
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); open(); }}
        aria-label="Preview media"
        title="Preview media"
        className="block w-full text-left"
      >
        {node}
      </button>
    );

    if (item.kind === 'draft') {
      const d = item.draft;
      return (
        <PostItem
          key={selKey(item)}
          {...common}
          thumb={previewable(<DraftThumb draft={d} className={thumbClass} />, () => setPreview({
            caption: d.caption,
            items: d.mediaItems.map((m) => ({
              type: m.type,
              url: m.url,
              thumbnail: m.type === 'video' ? d.coverImageUrl : null,
            })),
          }))}
          caption={d.caption}
          badge={(
            <span className="inline-flex items-center gap-1 rounded bg-amber-100 px-1.5 py-0.5 text-[11px] font-semibold text-amber-800">
              <Clock className="h-3 w-3" /> To be scheduled later
            </span>
          )}
          meta={[
            d.zernioAccountIds.length > 0
              ? `${d.zernioAccountIds.length} account${d.zernioAccountIds.length === 1 ? '' : 's'} chosen`
              : 'No accounts chosen yet',
            d.postType !== 'feed' ? d.postType.charAt(0).toUpperCase() + d.postType.slice(1) : null,
            d.scheduledFor ? `Planned for ${d.scheduledFor.replace('T', ' ')}` : null,
          ].filter(Boolean).join(' · ')}
          actions={(
            <>
              <Button
                size="sm"
                leftIcon={<Send className="h-3.5 w-3.5" />}
                title="Choose accounts, then schedule or publish this post."
                onClick={() => setOpenDraft(d)}
              >
                Schedule or publish
              </Button>
              <div className="flex gap-1">
                <Button size="sm" variant="ghost" title="Edit saved post" aria-label="Edit saved post" onClick={() => setOpenDraft(d)}>
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  title="Delete saved post"
                  aria-label="Delete saved post"
                  disabled={deleteDraftMut.isPending}
                  onClick={() => { if (window.confirm('Delete this saved post? It was never scheduled.')) deleteDraftMut.mutate(d.id); }}
                >
                  <Trash2 className="h-3.5 w-3.5 text-red-500" />
                </Button>
              </div>
            </>
          )}
        />
      );
    }

    const post = item.post;
    return (
      <PostItem
        key={selKey(item)}
        {...common}
        thumb={previewable(<SocialPostThumb post={post} className={thumbClass} iconClassName="h-5 w-5" />, () => setPreview({
          title: post.title ?? undefined,
          caption: post.content ?? '',
          items: previewItemsFromZernio(post.mediaItems),
        }))}
        title={post.title ?? undefined}
        caption={post.content ?? ''}
        badge={<Badge variant={statusVariant(post.status)} size="sm">{post.status}</Badge>}
        chips={post.platforms.map((p, i) => (
          <span
            key={`${p.platform}-${i}`}
            className={cn(
              'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium',
              p.status === 'failed'
                ? 'bg-red-50 text-red-700'
                : p.status === 'published'
                  ? 'bg-emerald-50 text-emerald-700'
                  : 'bg-amber-50 text-amber-700',
            )}
          >
            {platformLabel(p.platform)} · {p.status}
            {p.platformPostUrl && (
              <a href={p.platformPostUrl} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>
                <ExternalLink className="h-3 w-3" />
              </a>
            )}
          </span>
        ))}
        meta={post.scheduledFor
          ? `Scheduled for ${new Date(post.scheduledFor).toLocaleString()}`
          : post.createdAt ? `Created ${new Date(post.createdAt).toLocaleString()}` : ''}
        // Zernio decides what may still change: a scheduled or failed post can be
        // edited, and anything not yet published can be cancelled. The API
        // re-checks the status against Zernio before acting.
        actions={(canEditPost(post.status) || canDeletePost(post.status)) ? (
          <div className="flex gap-1">
            {canEditPost(post.status) && (
              <Button size="sm" variant="ghost" title="Edit scheduled post" aria-label="Edit scheduled post" onClick={() => setEditingPost(post)}>
                <Pencil className="h-3.5 w-3.5" />
              </Button>
            )}
            {canDeletePost(post.status) && (
              <Button
                size="sm"
                variant="ghost"
                title="Cancel scheduled post"
                aria-label="Cancel scheduled post"
                disabled={cancelMut.isPending}
                onClick={() => confirmCancel(post)}
              >
                <Trash2 className="h-3.5 w-3.5 text-red-500" />
              </Button>
            )}
          </div>
        ) : null}
      />
    );
  };

  return (
    <div className={embedded ? 'space-y-4' : 'p-6 space-y-6'}>
      {embedded ? (
        (!configured || !isVerified) && <div className="flex justify-end">{newPostButton}</div>
      ) : (
        <PageHeader
          title="Social Posts"
          subtitle="Publish or schedule posts across every social account connected in Zernio."
          actions={newPostButton}
        />
      )}

      {credentials.isLoading ? (
        <div className="flex items-center justify-center h-64"><Spinner /></div>
      ) : !configured ? (
        <EmptyState
          icon={<Share2 className="h-8 w-8" />}
          title="Zernio is not connected"
          description="Ask a Prospera administrator to add your Zernio key in Module Settings to start publishing."
        />
      ) : !isVerified ? (
        <Card>
          <CardBody className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
            <div className="text-sm">
              <div className="font-medium text-slate-800">Zernio key is unverified</div>
              <div className="text-slate-500 mt-0.5">
                Publishing stays disabled until we have a valid Zernio connection. Ask a
                Prospera administrator to check it in Module Settings.
              </div>
            </div>
          </CardBody>
        </Card>
      ) : (
        <>
          {/* ── Toolbar ─────────────────────────────────────────────────── */}
          <div className="flex flex-wrap items-center gap-3">
            <nav aria-label="Folder" className="flex items-center gap-1 text-sm min-w-0">
              {openFolder ? (
                <>
                  <button type="button" onClick={() => goToFolder(null)} className="text-slate-500 hover:text-slate-900 hover:underline">
                    All folders
                  </button>
                  <ChevronRight className="h-3.5 w-3.5 text-slate-400 shrink-0" aria-hidden="true" />
                  <span className="font-semibold text-slate-900 truncate" aria-current="page">{openFolder.name}</span>
                  {openFolder.id !== 0 && (
                    <span className="flex items-center ml-1">
                      <Button size="sm" variant="ghost" title="Rename folder" onClick={() => setNameDialog({ folder: openFolder })}>
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" title="Delete folder" onClick={() => setDeletingFolder(openFolder)}>
                        <Trash2 className="h-3.5 w-3.5 text-red-500" />
                      </Button>
                    </span>
                  )}
                </>
              ) : (
                <span className="font-semibold text-slate-900">All folders</span>
              )}
            </nav>

            <div className="w-56">
              <Select
                options={STATUS_OPTIONS}
                value={status}
                onChange={(v) => { setStatus((v as StatusFilter) ?? 'all'); clearSelection(); }}
              />
            </div>

            <div className="relative flex-1 min-w-[180px] max-w-xs">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder={openFolder ? `Search ${openFolder.name}...` : 'Search all folders...'}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>

            <div className="ml-auto flex items-center gap-2">
              {openFolder && viewToggle}
              <Button variant="secondary" leftIcon={<FolderPlus className="h-4 w-4" />} onClick={() => setNameDialog({ folder: null })}>
                New Folder
              </Button>
              {embedded && newPostButton}
            </div>
          </div>

          {credentials.data?.connectedPlatforms?.length ? (
            <div className="flex flex-wrap gap-1.5 -mt-1">
              {credentials.data.connectedPlatforms.map((p) => (
                <Badge key={p} variant="info" size="sm">{platformLabel(p)}</Badge>
              ))}
            </div>
          ) : null}

          {loading ? (
            <div className="flex items-center justify-center h-48"><Spinner /></div>
          ) : !openFolder ? (
            /* ── Folder tiles ──────────────────────────────────────────── */
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              {tiles.map(({ folder: f, count, matches: m }) => (
                <div key={f.id} className="group relative rounded-lg border border-slate-200 bg-white hover:border-primary/60 hover:shadow-sm transition">
                  <button type="button" onClick={() => goToFolder(f.id)} className="w-full text-left flex items-center gap-3 p-4">
                    <span className="shrink-0 rounded-md bg-primary/10 text-primary p-2.5">
                      <Folder className="h-5 w-5" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block font-medium text-slate-900 truncate">{f.name}</span>
                      <span className="block text-xs text-slate-500 mt-0.5">
                        {q ? `${m} match${m === 1 ? '' : 'es'}` : `${count} post${count === 1 ? '' : 's'}`}
                      </span>
                    </span>
                  </button>
                  {f.id !== 0 && (
                    <div className="absolute right-2 top-2 flex opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
                      <Button size="sm" variant="ghost" title="Rename folder" onClick={() => setNameDialog({ folder: f })}>
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" title="Delete folder" onClick={() => setDeletingFolder(f)}>
                        <Trash2 className="h-3.5 w-3.5 text-red-500" />
                      </Button>
                    </div>
                  )}
                </div>
              ))}
              {!q && (
                <button
                  type="button"
                  onClick={() => setNameDialog({ folder: null })}
                  className="rounded-lg border-2 border-dashed border-slate-300 text-slate-500 hover:border-primary hover:text-primary flex items-center justify-center gap-2 p-4 text-sm font-medium transition-colors"
                >
                  <FolderPlus className="h-4 w-4" />
                  New folder
                </button>
              )}
            </div>
          ) : (
            /* ── Inside a folder ───────────────────────────────────────── */
            <>
              {/* Drop targets while dragging: the other folders are not on
                  screen inside a folder, so they come to the drag instead. */}
              {dragKeys && (
                <div className="rounded-lg border border-dashed border-primary/50 bg-primary/5 p-2.5">
                  <p className="text-xs font-medium text-slate-600 mb-2">
                    Move {dragKeys.length === 1 ? 'post' : `${dragKeys.length} posts`} to:
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {otherFolders.map((f) => (
                      <div
                        key={f.id}
                        {...dropProps(f)}
                        className={cn(
                          'flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-sm bg-white transition-colors',
                          dropTargetId === f.id ? 'border-primary bg-primary/10 text-primary' : 'border-slate-200 text-slate-700',
                        )}
                      >
                        <Folder className="h-3.5 w-3.5" />
                        {f.name}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {selected.size > 0 && (
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm text-slate-500">{selected.size} selected</span>
                  <Button variant="ghost" size="sm" onClick={clearSelection}>Deselect all</Button>
                  <div className="relative">
                    <Button
                      variant="secondary"
                      size="sm"
                      leftIcon={<FolderOpen className="h-4 w-4" />}
                      rightIcon={<ChevronDown className="h-3 w-3" />}
                      disabled={moveMut.isPending}
                      onClick={() => setMoveOpen((o) => !o)}
                    >
                      Move
                    </Button>
                    {moveOpen && (
                      <div className="absolute z-20 mt-1 w-48 rounded-md border border-slate-200 bg-white shadow-lg py-1">
                        {otherFolders.map((f) => (
                          <button
                            key={f.id}
                            type="button"
                            className="block w-full text-left px-3 py-1.5 text-sm hover:bg-slate-50"
                            onClick={() => moveMut.mutate({ keys: Array.from(selected), target: f })}
                          >
                            {f.name}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {folderItems.length === 0 ? (
                <EmptyState
                  icon={<Share2 className="h-8 w-8" />}
                  title={q ? 'No posts in this folder match your search' : status === 'later' ? 'Nothing saved for later here' : 'This folder is empty'}
                  description={q ? undefined : 'Choose this folder in New Post, or drag posts here from another folder.'}
                  action={{ label: 'New Post', onClick: () => setComposerOpen(true) }}
                />
              ) : (
                <div className={view === 'grid'
                  ? 'grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3'
                  : 'space-y-2.5'}
                >
                  {folderItems.map(renderItem)}
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* A new post starts in the folder being looked at (General from the tiles). */}
      <ZernioPostComposer
        isOpen={composerOpen}
        folderId={openFolder?.id ?? 0}
        onClose={() => setComposerOpen(false)}
        onPublished={() => qc.invalidateQueries({ queryKey: ['zernio', 'posts'] })}
      />

      {/* Finishing a post saved for later: seeded exactly as it was left, in its folder. */}
      <ZernioPostComposer
        isOpen={openDraft !== null}
        draft={openDraft}
        folderId={openDraft ? placement.get(`draft:${openDraft.id}`) ?? 0 : 0}
        onClose={() => setOpenDraft(null)}
        onPublished={() => qc.invalidateQueries({ queryKey: ['zernio', 'posts'] })}
      />

      {/* Editing reuses the composer, seeded from the post: same media picker,
          same per-platform limits, same validation as creating one. */}
      <ZernioPostComposer
        isOpen={editingPost !== null}
        onClose={() => setEditingPost(null)}
        editingPost={editingPost}
        onPublished={() => {
          qc.invalidateQueries({ queryKey: ['zernio', 'posts'] });
          setEditingPost(null);
        }}
      />

      <SocialMediaPreviewModal
        isOpen={preview !== null}
        onClose={() => setPreview(null)}
        title={preview?.title}
        caption={preview?.caption}
        items={preview?.items ?? []}
      />
      <FolderNameDialog
        state={nameDialog}
        onClose={() => setNameDialog(null)}
        onSaved={(created) => {
          invalidateFolders();
          setNameDialog(null);
          if (created) goToFolder(created.id);
        }}
      />

      <ConfirmDialog
        isOpen={deletingFolder !== null}
        onClose={() => setDeletingFolder(null)}
        onConfirm={() => deletingFolder ? deleteFolderMut.mutateAsync(deletingFolder).then(() => undefined) : undefined}
        title={`Delete “${deletingFolder?.name ?? ''}”?`}
        message={deletingCount > 0
          ? `Its ${deletingCount} post${deletingCount === 1 ? '' : 's'} will move to General. Nothing is cancelled or deleted.`
          : 'The folder is empty.'}
        confirmLabel="Delete folder"
      />
    </div>
  );
}

/** One post as a picture card (grid) or a row (list); same content either way. */
function PostItem({
  view, thumb, title, caption, badge, chips, meta, actions, selected, dragging, onToggleSelect, dragProps,
}: {
  view: ViewMode;
  thumb: ReactNode;
  title?: string;
  caption: string;
  badge: ReactNode;
  chips?: ReactNode;
  meta?: string;
  actions?: ReactNode;
  selected: boolean;
  dragging: boolean;
  onToggleSelect: () => void;
  dragProps: { draggable: boolean; onDragStart: (e: DragEvent) => void; onDragEnd: () => void };
}) {
  const box = (
    <button
      type="button"
      onClick={onToggleSelect}
      className="shrink-0"
      aria-label={selected ? 'Deselect post' : 'Select post'}
    >
      <span className={cn(
        'w-4 h-4 rounded border-2 flex items-center justify-center transition-colors bg-white',
        selected ? 'bg-primary border-primary' : 'border-slate-300 hover:border-primary',
      )}>
        {selected && <Check className="h-2.5 w-2.5 text-white" />}
      </span>
    </button>
  );

  const frame = cn(
    'rounded-lg border bg-white transition cursor-grab active:cursor-grabbing',
    selected ? 'border-primary ring-1 ring-primary/40' : 'border-slate-200 hover:border-slate-300',
    dragging && 'opacity-50',
  );

  if (view === 'grid') {
    return (
      <div {...dragProps} className={cn('flex flex-col overflow-hidden', frame)}>
        <div className="relative">
          {thumb}
          <span className="absolute top-2 left-2 z-10">{box}</span>
          <span className="absolute top-2 right-2 z-10">{badge}</span>
        </div>
        <div className="flex-1 flex flex-col p-3 gap-2">
          <div className="flex-1 min-w-0">
            {title && <p className="text-sm font-semibold text-slate-900 truncate">{title}</p>}
            <p className="text-sm text-slate-700 line-clamp-3 whitespace-pre-wrap break-words">{caption || '—'}</p>
            {chips && <div className="flex flex-wrap gap-1 mt-1.5">{chips}</div>}
            {meta && <p className="text-[11px] text-slate-400 mt-1.5">{meta}</p>}
          </div>
          {actions && <div className="flex flex-col items-start gap-1">{actions}</div>}
        </div>
      </div>
    );
  }

  return (
    <div {...dragProps} className={cn('flex gap-3 p-3', frame)}>
      <span className="self-start mt-0.5">{box}</span>
      {thumb}
      <div className="flex-1 min-w-0">
        {title && <p className="text-sm font-semibold text-slate-900 truncate">{title}</p>}
        <p className="text-sm text-slate-700 line-clamp-2 whitespace-pre-wrap break-words">{caption || '—'}</p>
        {chips && <div className="flex flex-wrap gap-1 mt-1.5">{chips}</div>}
        {meta && <p className="text-[11px] text-slate-400 mt-1.5">{meta}</p>}
      </div>
      <div className="shrink-0 flex flex-col items-end gap-1.5">
        {badge}
        {actions && <div className="flex items-center gap-1">{actions}</div>}
      </div>
    </div>
  );
}

/** A saved post's first media item, showing its chosen thumbnail for a video. */
function DraftThumb({ draft, className }: { draft: SocialPostDraft; className?: string }) {
  const first = draft.mediaItems[0];
  if (!first) {
    return (
      <div className={cn('grid place-items-center bg-slate-100 text-slate-400', className)}>
        <ImageIcon className="h-5 w-5" aria-hidden="true" />
      </div>
    );
  }
  return (
    <div className={cn('relative overflow-hidden bg-slate-100', className)}>
      {draft.mediaItems.length > 1 && (
        <span className="absolute bottom-1 left-1 z-10 bg-black/70 text-white text-[10px] font-medium px-1 rounded">
          {draft.mediaItems.length}
        </span>
      )}
      {first.type === 'video' ? (
        <>
          {draft.coverImageUrl ? (
            <img src={draft.coverImageUrl} alt="" loading="lazy" className="h-full w-full object-cover" />
          ) : (
            <video src={first.url} muted playsInline preload="metadata" className="h-full w-full object-cover" />
          )}
          <span className="absolute inset-0 grid place-items-center">
            <Play className="h-5 w-5 fill-white text-white drop-shadow" aria-hidden="true" />
          </span>
        </>
      ) : (
        <img src={first.url} alt="" loading="lazy" className="h-full w-full object-cover" />
      )}
    </div>
  );
}

/** Create or rename a My Posts folder. */
function FolderNameDialog({
  state,
  onClose,
  onSaved,
}: {
  state: { folder: MyPostFolder | null } | null;
  onClose: () => void;
  onSaved: (created: MyPostFolder | null) => void;
}) {
  const toast = useToast();
  const renaming = state?.folder ?? null;
  const [name, setName] = useState('');
  const [lastState, setLastState] = useState(state);

  // Seed the name each time the dialog opens.
  if (state !== lastState) {
    setLastState(state);
    if (state) setName(state.folder?.name ?? '');
  }

  const saveMut = useMutation({
    mutationFn: () => renaming
      ? renameMyPostFolder(renaming.id, name.trim()).then(() => null)
      : createMyPostFolder(name.trim()),
    onSuccess: (created) => {
      toast.success(renaming ? 'Folder renamed' : 'Folder created');
      onSaved(created);
    },
    onError: (e) => toast.error(apiMessage(e, renaming ? 'Could not rename the folder' : 'Could not create the folder')),
  });

  const unchanged = renaming !== null && name.trim() === renaming.name;

  return (
    <Modal
      isOpen={state !== null}
      onClose={onClose}
      title={renaming ? 'Rename folder' : 'New folder'}
      size="sm"
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={saveMut.isPending}>Cancel</Button>
          <Button onClick={() => saveMut.mutate()} disabled={!name.trim() || unchanged || saveMut.isPending}>
            {renaming ? 'Rename' : 'Create folder'}
          </Button>
        </div>
      }
    >
      <form onSubmit={(e) => { e.preventDefault(); if (name.trim() && !unchanged) saveMut.mutate(); }}>
        <Input
          label="Folder name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={100}
          autoFocus
          placeholder="e.g. October campaign"
        />
      </form>
    </Modal>
  );
}
